import { useMemo, useState } from 'react';
import { Chip } from '@mui/material';
import dayjs from 'dayjs';
import { PrimaryTableHeader, TableDataType } from 'app/components/elements/TableCustom';
import { CriticalityChip } from 'app/pages/IssuesPage/components/CriticalityChip';
import { downloadCsv } from 'utils/downloadCsv';
import { DEFAULT_CRITICALITY_LEVELS, getCriticalityDisplay } from 'utils/issueCriticality';
import { MaintenanceIssues } from 'types/report';

export const COMPLETED_MAINTENANCE_STATUS_COLORS: Record<
  string,
  { bgcolor: string; color: string }
> = {
  RESOLVED: { bgcolor: '#DCFCE7', color: '#15803D' },
  ACTIVE: { bgcolor: '#FEE2E2', color: '#DC2626' }
};

export const COMPLETED_MAINTENANCE_TABLE_HEADERS: PrimaryTableHeader[] = [
  { label: 'Asset Name', width: 200 },
  { label: 'Client Name', width: 160 },
  { label: 'Location', width: 180 },
  { label: 'Description', width: 240 },
  { label: 'Assigned To', width: 160 },
  { label: 'Due Date', width: 130 },
  { label: 'Resolution Date', width: 150 },
  { label: 'Criticality', width: 120 },
  { label: 'Status', width: 120 }
];

const formatDate = (value?: string | null) => (value ? dayjs(value).format('YYYY-MM-DD') : '-');

export const completedMaintenanceStatusChip = (status: string) => (
  <Chip
    size="small"
    label={status}
    sx={{
      ...(COMPLETED_MAINTENANCE_STATUS_COLORS[status] || { bgcolor: '#F3F4F6', color: '#374151' }),
      fontWeight: 700,
      fontSize: 11
    }}
  />
);

export const buildCompletedMaintenanceRow = (issue: MaintenanceIssues): TableDataType => ({
  assetName: { value: issue.assetName || '-' },
  clientName: { value: issue.assetClientName || '-' },
  location: { value: issue.location || '-' },
  description: { value: issue.description || '-' },
  assignedTo: { value: issue.assignedTo?.name || '-' },
  dueDate: { value: formatDate(issue.dueDate) },
  resolutionDate: { value: formatDate(issue.resolutionDate) },
  criticality: {
    value: (
      <CriticalityChip
        criticality={getCriticalityDisplay(issue.criticalityKey, DEFAULT_CRITICALITY_LEVELS)}
      />
    )
  },
  status: { value: completedMaintenanceStatusChip(issue.status) }
});

const COMPLETED_MAINTENANCE_CSV_HEADERS = [
  { key: 'assetName', label: 'Asset Name' },
  { key: 'clientName', label: 'Client Name' },
  { key: 'location', label: 'Location' },
  { key: 'description', label: 'Description' },
  { key: 'assignedTo', label: 'Assigned To' },
  { key: 'dueDate', label: 'Due Date' },
  { key: 'resolutionDate', label: 'Resolution Date' },
  { key: 'criticality', label: 'Criticality' },
  { key: 'status', label: 'Status' }
];

export const exportCompletedMaintenanceCsv = (filename: string, issues: MaintenanceIssues[]) => {
  const rows = issues.map((issue) => ({
    assetName: issue.assetName,
    clientName: issue.assetClientName,
    location: issue.location,
    description: issue.description,
    assignedTo: issue.assignedTo?.name,
    dueDate: formatDate(issue.dueDate),
    resolutionDate: formatDate(issue.resolutionDate),
    criticality:
      getCriticalityDisplay(issue.criticalityKey, DEFAULT_CRITICALITY_LEVELS)?.label ??
      issue.criticalityKey,
    status: issue.status
  }));
  downloadCsv(filename, COMPLETED_MAINTENANCE_CSV_HEADERS, rows);
};

export const useCompletedMaintenanceFilters = (issues: MaintenanceIssues[]) => {
  const [search, setSearch] = useState('');
  const [client, setClient] = useState('');
  const [location, setLocation] = useState('');
  const [assignedTo, setAssignedTo] = useState('');

  const clientOptions = useMemo(() => {
    const unique = Array.from(
      new Set(issues.map((issue) => issue.assetClientName).filter(Boolean))
    ) as string[];
    return [
      { label: 'All Clients', value: '' },
      ...unique.sort().map((name) => ({ label: name, value: name }))
    ];
  }, [issues]);

  const locationOptions = useMemo(() => {
    const unique = Array.from(
      new Set(issues.map((issue) => issue.location).filter(Boolean))
    ) as string[];
    return [
      { label: 'All Locations', value: '' },
      ...unique.sort().map((name) => ({ label: name, value: name }))
    ];
  }, [issues]);

  const assignedToOptions = useMemo(() => {
    const unique = Array.from(
      new Set(issues.map((issue) => issue.assignedTo?.name).filter(Boolean))
    ) as string[];
    return [
      { label: 'All Assignees', value: '' },
      ...unique.sort().map((name) => ({ label: name, value: name }))
    ];
  }, [issues]);

  const filteredIssues = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    return issues.filter((issue) => {
      if (client && issue.assetClientName !== client) return false;
      if (location && issue.location !== location) return false;
      if (assignedTo && issue.assignedTo?.name !== assignedTo) return false;
      if (keyword) {
        const haystack = [
          issue.assetName,
          issue.assetClientName,
          issue.location,
          issue.description,
          issue.assignedTo?.name
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(keyword)) return false;
      }
      return true;
    });
  }, [issues, search, client, location, assignedTo]);

  const hasActiveFilters = Boolean(search || client || location || assignedTo);

  const clearFilters = () => {
    setSearch('');
    setClient('');
    setLocation('');
    setAssignedTo('');
  };

  return {
    search,
    setSearch,
    client,
    setClient,
    clientOptions,
    location,
    setLocation,
    locationOptions,
    assignedTo,
    setAssignedTo,
    assignedToOptions,
    filteredIssues,
    hasActiveFilters,
    clearFilters
  };
};
